import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {OverlayPanel} from "primereact/overlaypanel";
import {Tooltip} from "primereact/tooltip";


import {exportFile, formValid, getKeyValue, isAdmin, subscription, user} from "../../utils/Utils";
import {InputTextarea} from "primereact/inputtextarea";
import {ErrorService} from "../../service/ErrorService";
import ScrapeStatus from "../Product/ScrapeStatus";

import {CampaignService} from "../../service/CampaignService";
import {AppContext} from "../../HomePage";
import CampaignEdit from "./CampaignEdit";
import CampaignWizard from "./CampaignWizard";
import {usage_types, usageTypeMeta} from "../../utils/Constants";
import {CampaignContext} from "../../App";
import {takeWizardResume} from "../../utils/amazonOAuth";
import {Skeleton} from "primereact/skeleton";
import {toDateString} from "../../utils/TimeUtil";
import {ExportService} from "../../service/ExportService";
import {InputText} from "primereact/inputtext";
import {Dialog} from "primereact/dialog";
import {ExportAsyncService} from "../../service/ExportAsyncService";
import {DashboardService} from "../../service/DashboardService";
import {MARKETPLACES} from "../Onboarding/MarketplaceConnectStep";
import {isMarketplaceOperatorCampaign, MARKETPLACE_OPERATOR_META} from "./marketplaceOperatorSettings";

// Display order of the campaign-type sections on the card grid. Marketplace
// buybox campaigns are a synthetic group (they pin settings.usage_type to
// 'seller', see isMarketplaceBuybox below), and so are marketplace operator
// campaigns (usage_type 'seller' + settings.marketplace_operator); the rest are
// plain usage types.
const CAMPAIGN_GROUP_ORDER = ['marketplace_buybox', 'marketplace_operator', 'seller', 'manufacturer', 'market_search', 'drop_shipping'];

// A campaign tracks wholesale suppliers when settings.supplier_module.is_active
// is on (the same flag every other supplier surface gates on). Exported as a
// pure predicate so the card badge and its wrapper condition can share it, and
// so it is testable without mounting the campaign list.
export const isSupplierCampaign = (c) => !!c.settings?.supplier_module?.is_active;

const Campaign = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const {campaigns, selectCampaign, loadCampaigns, updateCampaigns, toggleDownloadReports} = React.useContext(CampaignContext);

    const pages = {
        list: 'list',
        add: 'add',
        edit: 'edit'
    };

    const [page, setPage] = useState(pages.list);
    const [showWizard, setShowWizard] = useState(false);
    // Set when the browser comes back from Amazon's consent page mid-wizard, so
    // the wizard reopens on the step the seller left rather than dropping them
    // on this list with the dialog shut. Read once, on mount, because the
    // callback page navigates here (see takeWizardResume -- it clears the value,
    // so a later visit never reopens the wizard on its own).
    const [wizardResume, setWizardResume] = useState(null);
    useEffect(() => {
        const resume = takeWizardResume();
        if (!resume) return;
        setWizardResume(resume);
        setShowWizard(true);
    }, []);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const [loadingExport, setLoadingExport] = useState(false);
    const [campaignNameToDelete, setCampaignNameToDelete] = useState("");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [rowData, setRowData] = useState(null);
    const [search, setSearch] = useState('');
    // Campaign-type filter for the grouped card grid (null = all types).
    const [typeFilter, setTypeFilter] = useState(null);
    const cardMenu = useRef(null);
    const [menuCampaign, setMenuCampaign] = useState(null);
    // Per-campaign dynamic-pricing suggestion counts (lazy-loaded for
    // competitive campaigns with dynamic pricing enabled). id -> {higher, lower}
    // or 'loading'.
    const [pricingStats, setPricingStats] = useState({});

    const [showReport, setShowReport] = useState(false);
    const [reportCampaign, setReportCampaign] = useState(null);
    const [reportText, setReportText] = useState('');
    const [sendingReport, setSendingReport] = useState(false);

    const campaignService = new CampaignService();
    const exportService = new ExportService();
    const exportAsyncService = new ExportAsyncService();
    const dashboardService = new DashboardService();
    const errorService = new ErrorService();

    useEffect(() =>{
        if (selectCampaign)
            loadCampaigns(true);
        return () => {
            if(toast.current)
                toast.current.clear();
        }
    }, []);


    const add = () => {
        selectCampaign(null).then(()=> {
            setFormErrors({});
            setShowWizard(true);
        });
    };

    const edit = (rowData) => {
        selectCampaign(rowData.id).then(()=> {
            setFormErrors({});
            setPage(pages.edit);
            setShowEditDialog(true);
        });
    };

    const save = (campaign) => {
        checkErrors(campaign).then(formErrors=> {
            if (formValid(formErrors)) {
                if (page === pages.add)
                {
                    campaignService.addCampaign(campaign.name, campaign.use_sale_value, campaign.is_parent, campaign.parent_campaign, campaign.settings).then(savedCampaign => {
                        savedCampaign.scraping_requests = [];
                        updateCampaignInList(savedCampaign);
                        setPage(pages.list);
                        toast.current.show({severity: 'success', summary: t('campaign.add_campaign'), detail: t('message_detail.successful') + " "});
                    }).catch(error =>{
                        setError(error);
                        setError(null);
                    });
                }
                else if (page === pages.edit)
                {
                    campaignService.editCampaign(campaign.id, campaign.name, campaign.use_sale_value, campaign.is_parent, campaign.parent_campaign, campaign.settings).then(() => {
                        updateCampaignInList(campaign);
                        setShowEditDialog(false);
                        setPage(pages.list);
                        toast.current.show({severity: 'success', summary: t('campaign.edit_campaign'), detail: t('message_detail.successful')});
                    }).catch(error =>{
                        setError(error);
                        setError(null);
                    });
                }

            }
            else
                setFormErrors(formErrors);
        });

    };

    const updateCampaignInList = (campaign) => {
        const index = campaigns.findIndex(c => c.id === campaign.id);
        if (index === -1)
            campaigns.unshift(campaign);
        else
            campaigns[index] = campaign;
        updateCampaigns([...campaigns]);
    };

    const deleteCampaign = (campaign) => {
        if(campaign.name === campaignNameToDelete)
            campaignService.deleteCampaign(campaign.id).then(() => {
                let filteredCampaigns = campaigns.filter(c => c.id !== campaign.id);
                updateCampaigns([...filteredCampaigns]);
                toast.current.show({severity: 'success', summary: t('campaign.delete_campaign'), detail: t('message_detail.successful')});
            }).catch(error =>{
                setError(error);
                setError(null);
            });
        else
            toast.current.show({severity: 'warn', summary: t('campaign.delete_campaign_name_wrong'), detail: t('message_detail.failed')});
    }


    const checkError = (key, value) => {
        let errorText = "";
        if (key === "name") {
            errorText =  value.length > 0 ? "" : t('validations.invalid_name');
        }else if (key === "use_sale_value") {
            errorText =  value !== null && value !== undefined ? "" : t('validations.invalid_value');
        }
        return errorText;
    }

    const checkErrors = async (campaign) => {
        let errors = { ...formErrors };
        Object.entries(campaign).forEach(([key, value]) => {
            errors[key] = checkError(key, value);
        });
        return errors;
    };

    const exportDetailedProductList = (campaign) => {
        setLoadingExport(true);
        exportAsyncService.exportDetailedProductList(campaign.id, null, null).then((response) => {
            setLoadingExport(false);
            toggleDownloadReports(true);
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoadingExport(false);
        });
        // exportService.exportDetailedProductList(campaign.id, null, null).then((response) => {
        //     exportFile(response, campaign.name + "_" + t('dashboard.excel_product_list') + "_" + toDateString(new Date) + ".xlsx");
        //     setLoadingExport(false);
        // }).catch(error =>{
        //     setError(error);
        //     setError(null);
        //     setLoadingExport(false);
        // });
    };

    const exportCompetitorList = (campaign) => {
        setLoadingExport(true);
        exportService.exportCompetitorPriceList(campaign.id, new Date()).then((response) => {
            exportFile(response, campaign.name + "_" + t('dashboard.excel_competitor_list') + "_" + toDateString(new Date) + ".xlsx");
            setLoadingExport(false);
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoadingExport(false);
        });
    };

    const exportProductReviews = (campaign) => {
        setLoadingExport(true);
        exportService.exportProductReviews(campaign.id).then((response) => {
            exportFile(response, campaign.name + "_" + t('dashboard.csv_product_reviews') + "_" + toDateString(new Date) + ".xlsx");
            setLoadingExport(false);
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoadingExport(false);
        });
    };

    const exportProductSuggestions = (campaign) => {
        setLoadingExport(true);
        exportAsyncService.exportProductSuggestions(campaign.id).then((response) => {
            setLoadingExport(false);
            toggleDownloadReports(true);
        }).catch(error =>{
            setError(error);
            setError(null);
            setLoadingExport(false);
        });
    };


    const openReport = (campaign) => {
        setReportCampaign(campaign);
        setReportText('');
        setShowReport(true);
    };

    const sendReport = () => {
        if (!reportText.trim()) return;
        setSendingReport(true);
        const c = reportCampaign;
        // Kampanya bazlı "Hata Bildir" → error reporting'e yazılır (admin listesi +
        // e-posta bildirimi). type 'campaign_issue' ile ürün/scraping hatalarından
        // ayrışır; ürün/scraping_request/rakip alanları null (kampanya seviyesi).
        errorService.addError(null, c.id, null, null, reportText.trim(), 'campaign_issue').then(() => {
            toast.current.show({severity: 'success', summary: t('campaign.report.sent', 'Bildiriminiz iletildi'), detail: t('message_detail.successful')});
            setShowReport(false);
            setReportText('');
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setSendingReport(false));
    };

    // Open the unified onboarding (setup/management) flow for a campaign.
    const openOnboarding = (rowData) => {
        selectCampaign(rowData.id).then(() => { window.location = '#/onboarding'; });
    };

    // ---- card-grid helpers ---------------------------------------------------
    // Marketplace API campaigns (settings.marketplace_api present) pin
    // settings.usage_type to 'seller' (see CampaignEdit.js selectMarketplaceBuybox)
    // -- type grouping must not read that as "Competitive"; it needs its own
    // branch ahead of the plain usage_type lookup (see campaignGroupKey below).
    const isMarketplaceBuybox = (c) => !!c.settings?.marketplace_api;
    const isShopifyCampaign = (c) => !!(c.settings && (c.settings.shopify_oauth || c.settings.shop));
    // Marketplace API campaigns (Trendyol/Hepsiburada/Amazon connect) carry
    // settings.marketplace_api.marketplace — show that marketplace's display name
    // and brand color. Looked up from the same MARKETPLACES list the connect step's
    // picker uses (MarketplaceConnectStep.js), so a badge here never drifts from
    // the card shown during connect; the fallbacks only matter for a marketplace
    // key not yet added there.
    const marketplaceApiName = (c) => {
        const m = c.settings && c.settings.marketplace_api && c.settings.marketplace_api.marketplace;
        if (!m) return null;
        const found = MARKETPLACES.find(x => x.key === m);
        return found ? found.label : m.charAt(0).toUpperCase() + m.slice(1);
    };
    const marketplaceApiColor = (c) => {
        const m = c.settings && c.settings.marketplace_api && c.settings.marketplace_api.marketplace;
        const found = MARKETPLACES.find(x => x.key === m);
        return found ? found.color : '#6b7280';
    };
    const goToCampaign = (c) => { selectCampaign(c.id).then(() => { window.location = '#/product'; }); };
    // Surface the toolbar search whenever there is anything to filter.
    const showSearch = (campaigns || []).length > 1;

    // A styled row for the per-card overflow menu: soft colored icon badge,
    // bold label + muted description, hover highlight.
    const menuItem = ({icon, color, bg, label, desc, onClick, loading, danger}) => (
        <div className="cmpz-menu-item" onClick={onClick}
             style={{display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderRadius: 10, cursor: 'pointer'}}>
            <span style={{width: 34, height: 34, borderRadius: 9, background: bg, color: color, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <i className={loading ? 'pi pi-spin pi-spinner' : icon} style={{fontSize: '1.05rem'}}/>
            </span>
            <span style={{display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25}}>
                <span style={{fontWeight: 600, fontSize: '.9rem', color: danger ? '#dc2626' : '#111827'}}>{label}</span>
                {desc && <span style={{fontSize: '.74rem', color: '#9ca3af'}}>{desc}</span>}
            </span>
        </div>
    );

    const usageTypeOf = (c) => c.settings?.usage_type || user().usage_type;
    // A campaign is "competitive" (rekabet) when its usage type is seller, and
    // dynamic pricing is "configured" when the campaign pricing strategy is
    // active. Only these campaigns get the price-up/down suggestion metrics.
    const isCompetitive = (c) => usageTypeOf(c) === 'seller';
    const isBrandProtection = (c) => usageTypeOf(c) === 'manufacturer';
    const isDynamicPricingOn = (c) => !!(c.pricing_strategy && c.pricing_strategy.is_active);
    const showsPricingSuggestions = (c) => isCompetitive(c) && isDynamicPricingOn(c);
    // Campaigns that need a lazy overview fetch for extra card metrics.
    const needsOverview = (c) => showsPricingSuggestions(c) || isBrandProtection(c);

    // Price-check status icon in the card's top-right corner. Admins always get
    // it (it doubles as their "run a campaign price check now" button); customers
    // get it read-only, and only once a run exists -- an empty request would
    // otherwise render as a green "done" for a campaign that never ran.
    const showsScrapeStatus = (c) => isAdmin() || !!c.prices_campaign_update_scraping_request;
    // Admin-only campaigns: the backend hides them from customers, so only an
    // admin ever receives one; the tag tells the admin the customer cannot see it.
    const isHiddenCampaign = (c) => isAdmin() && c.settings?.admin_only === true;
    // A catalogue campaign's sellers are priced by its target campaign's price
    // check, which copies each price here; the catalogue's own price check
    // (like any campaign's) runs on its own schedule and scans only its other
    // (non-seller) products.
    const isCatalog = (c) => !!(c.settings && c.settings.catalog_of_campaign_id);
    // The campaign a catalogue feeds, named on the card's catalogue badge.
    const catalogTargetName = (c) => ((campaigns || []).find(x => x.id === c.settings.catalog_of_campaign_id) || {}).name || '';

    // ---- per-card warnings (consolidated into one health icon) --------------
    // Each entry: {sev: 'warn'|'error', text}. The card shows a single icon in
    // the top-right; tooltip lists every active warning.
    const cardWarnings = (c) => {
        const w = [];
        if ((c.product_count ?? 0) === 0) {
            w.push({sev: 'warn', text: t('campaign.warn.noProducts', 'Henüz ürün eklenmemiş')});
        } else {
            // Not: kampanya-seviyesi "izlenen site yok" (noMonitoredSites) uyarısı
            // kaldırıldı — seller'lar ürünlere ekli olduğunda rakipler ürün
            // bazında tutulur, kampanya competitors dizisi boş olsa da fiyat
            // toplanır. Rakibi olmayan ürünler zaten noCompetitors ile uyarılıyor.
            // No campaign-level strategy AND no product with its own active
            // product-specific strategy — only then is dynamic pricing truly off.
            if (isCompetitive(c) && !isDynamicPricingOn(c) &&
                !((c.products_with_pricing_strategy_count || 0) > 0))
                w.push({sev: 'warn', text: t('campaign.dynamicPricingNotSet', 'Dinamik fiyatlama yapılandırılmamış')});
            if (showsPricingSuggestions(c) && (c.products_without_min_price_count || 0) > 0)
                w.push({sev: 'warn', text: t('campaign.warn.noMinPrice', '{n} üründe minimum fiyat koruması yok; bu ürünler için dinamik fiyatlandırma çalışmaz.').replace('{n}', c.products_without_min_price_count)});
            if (isBrandProtection(c) && (c.products_without_map_count || 0) > 0)
                w.push({sev: 'warn', text: t('campaign.mapNotSet', '{n} üründe MAP değeri ayarlanmamış').replace('{n}', c.products_without_map_count)});
            if ((c.products_without_competitors_count || 0) > 0)
                w.push({sev: 'warn', text: t('campaign.warn.noCompetitors', '{n} üründe rakip yok').replace('{n}', c.products_without_competitors_count)});
        }
        return w;
    };
    const warningCount = (c) => cardWarnings(c).length;
    const hasWarning = (c) => warningCount(c) > 0;
    const hasError = (c) => cardWarnings(c).some(x => x.sev === 'error');
    // Prefix each line with a colored severity marker (errors red, warnings amber).
    const sevMark = (sev) => sev === 'error' ? '🛑' : '⚠️';
    const warningTooltip = (c) => cardWarnings(c).map(x => `${sevMark(x.sev)}  ${x.text}`).join('\n');

    // Lazily fetched overview metric, or undefined while it is still loading.
    const stat = (c, key) => (pricingStats[c.id] && pricingStats[c.id] !== 'loading') ? pricingStats[c.id][key] : undefined;
    // A metric earns its color only once it has something to say; zeros render
    // neutral so a healthy card reads calm instead of like an alarm board.
    const metricColor = (value, color) => (value > 0 ? color : '#6b7280');

    // ---- campaign types ------------------------------------------------------
    // Type is a sort key and a filter, not a section: cards of the same type sit
    // together in one continuous grid. The toolbar chips (shown once the account
    // has more than one type) hold the per-type census and drive the filter.
    const campaignGroupKey = (c) => {
        if (isMarketplaceBuybox(c)) return 'marketplace_buybox';
        if (isMarketplaceOperatorCampaign(c)) return 'marketplace_operator';
        const ut = usageTypeOf(c);
        return CAMPAIGN_GROUP_ORDER.includes(ut) ? ut : 'seller';
    };
    const typeCounts = (campaigns || []).reduce((acc, c) => {
        const k = campaignGroupKey(c);
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});
    const groupingActive = Object.keys(typeCounts).length > 1;
    // Ignore a stale type filter once its last campaign is gone.
    const activeTypeFilter = typeFilter && typeCounts[typeFilter] ? typeFilter : null;
    const filteredCampaigns = (campaigns || []).filter(c =>
        (!search || (c.name || '').toLowerCase().includes(search.toLowerCase())) &&
        (!activeTypeFilter || campaignGroupKey(c) === activeTypeFilter));
    // One flat grid. Cards are SORTED by the section order so campaigns of the
    // same type still sit together, but without section headers splitting the
    // grid into half-empty rows. The toolbar type chips carry the per-type
    // census and double as the filter, so no count is printed twice.
    const sortedCampaigns = filteredCampaigns
        .map((c, i) => ({c, i}))
        .sort((a, b) => (CAMPAIGN_GROUP_ORDER.indexOf(campaignGroupKey(a.c)) - CAMPAIGN_GROUP_ORDER.indexOf(campaignGroupKey(b.c)))
            || (a.i - b.i))
        .map(x => x.c);
    const groupMeta = (key) => {
        if (key === 'marketplace_buybox') return {icon: 'pi pi-shopping-bag', color: '#7c3aed', bg: '#ede9fe'};
        if (key === 'marketplace_operator') return MARKETPLACE_OPERATOR_META;
        return usageTypeMeta(key);
    };
    const groupLabel = (key) => {
        if (key === 'marketplace_buybox') return t('campaign.usage_type_marketplace_buybox');
        if (key === 'marketplace_operator') return t('campaign.usage_type_marketplace_operator');
        const found = usage_types(t).find(x => x.value === key);
        return found ? found.label : key;
    };
    const typeChipStyle = (key, selected) => {
        const meta = key ? groupMeta(key) : null;
        return {
            borderRadius: 999, padding: '4px 12px', fontSize: '.8rem', cursor: 'pointer', userSelect: 'none',
            background: meta ? meta.bg : '#f3f4f6', color: meta ? meta.color : '#374151',
            fontWeight: selected ? 700 : 400,
            boxShadow: selected ? 'inset 0 0 0 1.5px currentColor' : 'none'
        };
    };
    const cardGridStyle = {display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16};

    // ---- price-check schedule info ------------------------------------------
    // scraping_jobs hours are stored in UTC; show them in the user's local time.
    const utcHourToLocal = (hhmm) => {
        const parts = (hhmm || '').split(':');
        if (parts.length !== 2) return hhmm;
        const d = new Date();
        d.setUTCHours(parseInt(parts[0], 10), parseInt(parts[1], 10), 0, 0);
        const lh = String(d.getHours()).padStart(2, '0');
        const lm = String(d.getMinutes()).padStart(2, '0');
        return `${lh}:${lm}`;
    };
    const priceCheckInfo = (c) => {
        const perDay = c.settings?.daily_price_check_count || subscription()?.daily_price_check_count || 0;
        if (!perDay) return null;
        const scheduled = c.settings?.scraping_jobs || [];
        // Only rows the scheduler would run count, as on the scheduling screen:
        // Otomatik Fiyat Kontrol on and at least one weekday (job_days [7] = never).
        const runsSomeDay = (jd) => !jd || jd.length === 0 || jd.some(d => d >= 0 && d <= 6);
        const effective = scheduled.filter(j => j.auto_price_update && runsSomeDay(j.job_days));
        if (scheduled.length > 0 && effective.length === 0) return {off: true};
        const jobs = effective.slice()
            .sort((a, b) => (a.hour || '').localeCompare(b.hour || ''))
            .slice(0, perDay);
        const times = jobs.map(j => utcHourToLocal(j.hour)).filter(Boolean);
        return {perDay, times};
    };
    // Marketplace API campaigns poll on a fixed cadence (refresh_interval_minutes)
    // instead of the legacy per-time scraping_jobs schedule, so the card shows a
    // frequency phrase instead of priceCheckInfo's times list. Reuse the exact
    // frequency labels defined for the onboarding price-check step (see
    // MarketplacePriceCheckStep.js frequencyLabel) so the wording matches
    // between the campaign card and the onboarding/admin control.
    const marketplaceRefreshLabel = (c) => {
        const minutes = c.settings?.marketplace_api?.refresh_interval_minutes || 60;
        const freq = t(`onboarding.marketplacePriceCheck.frequency.${minutes}`, `every ${minutes} minutes`);
        return t('campaign.refreshFrequency', 'Refresh: {frequency}').replace('{frequency}', freq);
    };

    // Lazily fetch overview-derived metrics (pricing suggestions / MAP-MRP
    // violations) for qualifying campaigns.
    useEffect(() => {
        const today = toDateString(new Date());
        (campaigns || []).forEach(c => {
            if (needsOverview(c) && pricingStats[c.id] === undefined) {
                setPricingStats(prev => ({...prev, [c.id]: 'loading'}));
                dashboardService.getOverview(c.id, today).then(overview => {
                    setPricingStats(prev => ({...prev, [c.id]: {
                        higher: overview?.total_higher_suggested_price ?? 0,
                        lower: overview?.total_lower_suggested_price ?? 0,
                        violatedMap: overview?.total_violated_map ?? 0,
                        violatedMrp: overview?.total_violated_mrp ?? 0
                    }}));
                }).catch(() => {
                    setPricingStats(prev => ({...prev, [c.id]: {higher: 0, lower: 0, violatedMap: 0, violatedMrp: 0}}));
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaigns]);

    // Account-wide product totals for the summary strip in the toolbar.
    const totalActiveProducts = (campaigns || []).reduce((a, c) => a + (c.product_count ?? 0), 0);
    const totalPassiveProducts = (campaigns || []).reduce((a, c) => a + (c.inactive_product_count ?? 0), 0);
    // Near the plan's product limit: a warning icon on the total, explained on hover
    // (it used to be a banner on every page).
    const productLimit = parseInt(subscription()?.total_product_count) || 0;
    const isNearProductLimit = productLimit > 0 && totalActiveProducts > productLimit * 0.95;
    const isProductLimitFull = productLimit > 0 && totalActiveProducts >= productLimit;

    const returnPageList = () => {
        setPage(pages.list)
    }

    return (
        <React.Fragment>
            <div className="card">
                {/* toolbar: add (unless single-store Shopify merchant) + search */}
                {/* single toolbar row: add + search + type filter chips + the
                    account-wide product totals pinned right (chips wrap below on
                    narrow screens; they only render once several campaign types
                    coexist) */}
                <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18}}>
                    {!(user()?.settings?.shopify_app?.shop) &&
                        <Button icon="pi pi-plus" data-tour="step7" className="p-button-success"
                                onClick={() => add()} label={t('campaign.add_campaign')}/>}
                    {showSearch &&
                        <span className="p-input-icon-left" style={{flex: '1 1 200px', maxWidth: 300}}>
                            <i className="pi pi-search"/>
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} style={{width: '100%'}}
                                       placeholder={t('campaign.search', 'Kampanya ara')}/>
                        </span>}
                    {groupingActive &&
                        <div className="preventSelect" style={{display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center'}}>
                            <span onClick={() => setTypeFilter(null)} style={typeChipStyle(null, activeTypeFilter === null)}>
                                {t('campaign.group.all', 'All')} {(campaigns || []).length}
                            </span>
                            {CAMPAIGN_GROUP_ORDER.filter(k => typeCounts[k]).map(k =>
                                <span key={k} onClick={() => setTypeFilter(activeTypeFilter === k ? null : k)}
                                      style={typeChipStyle(k, activeTypeFilter === k)}>
                                    {groupLabel(k)} {typeCounts[k]}
                                </span>)}
                        </div>}
                    {/* Account-wide product totals, pushed to the far right of the toolbar. */}
                    <div style={{marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10, background: '#f9fafb',
                        border: '1px solid #eef0f3', borderRadius: 10, padding: '9px 14px'}}>
                        <span style={{width: 28, height: 28, borderRadius: 8, background: '#dbeafe', color: '#2563eb', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <i className="pi pi-box" style={{fontSize: '.85rem'}}/>
                        </span>
                        <span style={{fontSize: '.85rem', color: '#374151'}}>
                            <b style={{fontSize: '.95rem', color: '#16a34a'}}>{totalActiveProducts}</b> {t('campaign.footerActive', 'active products')}
                            {isNearProductLimit &&
                                <i className="pi pi-exclamation-triangle campaign-limit-warning"
                                   data-pr-tooltip={t('product.run_out_of_products', {total_products: totalActiveProducts, subs_product_count: productLimit})}
                                   aria-label={t('product.run_out_of_products', {total_products: totalActiveProducts, subs_product_count: productLimit})}
                                   style={{marginLeft: 6, fontSize: '.85rem', cursor: 'help', color: isProductLimitFull ? '#dc2626' : '#d97706'}}/>}
                            <span style={{color: '#9ca3af', margin: '0 8px'}}>·</span>
                            <b style={{fontSize: '.95rem', color: '#6b7280'}}>{totalPassiveProducts}</b> {t('campaign.footerPassive', 'passive products')}
                        </span>
                    </div>
                </div>

                {/* Re-key so the tooltip re-scans the DOM after cards and the
                    async pricing metrics finish loading. */}
                <Tooltip key={`tt-${filteredCampaigns.length}-${Object.keys(pricingStats).length}`}
                         target=".campaign-metric" position="top"/>
                <Tooltip key={`ttl-${isNearProductLimit}`} target=".campaign-limit-warning" position="bottom"/>
                {/* Card CSS. The action row is always in normal flow -- nothing is
                    hidden behind hover -- so the affordance is discoverable on touch
                    and pointer alike. */}
                <style>{`.campaign-warning-tt.p-tooltip{max-width:380px}.campaign-warning-tt .p-tooltip-text{white-space:pre-line;max-width:380px;line-height:1.6;font-size:.86rem;padding:.7rem .85rem}
.campaign-card{transition:box-shadow .15s ease,border-color .15s ease}
.campaign-card:hover{box-shadow:0 4px 14px rgba(0,0,0,.10);border-color:#d1d5db}
.campaign-card .campaign-metrics{background:#f9fafb;border-radius:8px;padding:8px 10px}
.campaign-card .campaign-metric{flex:1 1 0;min-width:fit-content}
.campaign-card .campaign-health .p-button.p-button-rounded{width:24px;height:24px;padding:0}
.campaign-card .campaign-health .p-button.p-button-rounded .p-button-icon{font-size:.8rem}
`}</style>
                <Tooltip key={`ttw-${filteredCampaigns.reduce((n, c) => n + warningCount(c), 0)}`}
                         className="campaign-warning-tt" target=".campaign-warning" position="left"/>
                {filteredCampaigns.length === 0
                    ? <Message severity="info" text={t('campaign.no_campaign')}/>
                    : <div data-tour="step2" style={cardGridStyle}>
                        {sortedCampaigns.map(c => {
                            return (
                                <div key={c.id} className="campaign-card" onClick={() => goToCampaign(c)} title={t('campaign.openProducts', 'Ürünleri aç')}
                                    style={{position: 'relative', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', cursor: 'pointer',
                                    padding: 16, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)',
                                    overflow: 'hidden'}}>
                                    {/* The health icons overlap the tinted header band. Each sits in
                                        its own white disc so an amber warning stays legible on an
                                        amber (market research) band and a red error on a red (brand)
                                        band, without the two reading as one blob. */}
                                    {(showsScrapeStatus(c) || hasWarning(c)) &&
                                        <span className="preventSelect campaign-health" style={{position: 'absolute', top: 10, right: 12, display: 'flex', gap: 6, alignItems: 'center'}}
                                              onClick={(e) => e.stopPropagation()}>
                                            {hasWarning(c) &&
                                                <span style={{width: 24, height: 24, borderRadius: '50%', background: '#fff', display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                                                    <i className="campaign-warning pi pi-exclamation-triangle"
                                                       data-pr-tooltip={warningTooltip(c)} data-pr-position="left"
                                                       style={{color: hasError(c) ? '#dc2626' : '#f59e0b', fontSize: '.85rem'}}/>
                                                </span>}
                                            {showsScrapeStatus(c) &&
                                                <span style={{borderRadius: '50%', background: '#fff', display: 'flex', flexShrink: 0}}>
                                                    <ScrapeStatus type="price" compact scraping_request={c.prices_campaign_update_scraping_request}
                                                                  campaign_id={c.id} product_id={null} competitor_url={null}
                                                                  readOnly={!isAdmin()}
                                                                  newRequest={isAdmin() ? {type: 'prices_campaign_update', campaign_id: c.id} : undefined}
                                                                  newRequestCompleted={() => loadCampaigns(true)}/>
                                                </span>}
                                        </span>}
                                    {/* Slim header band tinted with the campaign type's colour (the
                                        same palette as the toolbar type chips and the title icon), so
                                        the type reads at a glance while scanning the grid. Only the
                                        name lives on the band; badges sit below it on white so their
                                        own colours (amber "not connected", marketplace brands) never
                                        fight the tint. Negative margins let the band bleed to the card
                                        edge; the card clips it to the rounded corners. */}
                                    <div title={c.name} style={{display: 'flex', alignItems: 'center', gap: 7, margin: '-16px -16px 0', padding: '11px 16px',
                                        background: groupMeta(campaignGroupKey(c)).bg,
                                        paddingRight: 16 + (showsScrapeStatus(c) ? 32 : 0) + (hasWarning(c) ? 30 : 0)}}>
                                        <i className={groupMeta(campaignGroupKey(c)).icon}
                                           style={{color: groupMeta(campaignGroupKey(c)).color, fontSize: '.95rem', flexShrink: 0}}/>
                                        <span style={{fontWeight: 800, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{c.name}</span>
                                    </div>
                                    {(isShopifyCampaign(c) || marketplaceApiName(c) || isMarketplaceBuybox(c) || isSupplierCampaign(c) || isCatalog(c) || isHiddenCampaign(c)) &&
                                        <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: -4}}>
                                            {isShopifyCampaign(c) && <span style={{background: '#95BF47', color: '#fff', borderRadius: 4, fontSize: '.7rem', padding: '2px 6px', flexShrink: 0}}>Shopify</span>}
                                            {marketplaceApiName(c)
                                                ? <span style={{
                                                    background: marketplaceApiColor(c),
                                                    color: '#fff', borderRadius: 4, fontSize: '.7rem', padding: '2px 6px', flexShrink: 0
                                                  }}>{marketplaceApiName(c)}</span>
                                                : isMarketplaceBuybox(c) &&
                                                  <span style={{
                                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                                      background: '#fef3c7', color: '#92400e', borderRadius: 4,
                                                      fontSize: '.7rem', padding: '2px 6px', flexShrink: 0
                                                  }}>
                                                      <i className="pi pi-exclamation-triangle" style={{fontSize: '.65rem'}}/>
                                                      {t('campaign.marketplaceNotConnected', 'Not connected — finish setup')}
                                                  </span>}
                                            {isSupplierCampaign(c) && <span style={{background: '#6366f1', color: '#fff', borderRadius: 4, fontSize: '.7rem', padding: '2px 6px', flexShrink: 0}}>{t('campaign.supplierModuleBadge', 'Tedarikçi')}</span>}
                                            {isHiddenCampaign(c) && <span title={t('onboarding.settings.help.admin_only')}
                                                                          style={{background: '#6b7280', color: '#fff', borderRadius: 4, fontSize: '.7rem', padding: '2px 6px', flexShrink: 0}}>{t('campaign.hidden_badge')}</span>}
                                            {isCatalog(c) && <span title={t('campaign.catalogBadgeHint', {campaign: catalogTargetName(c)})}
                                                                   style={{background: '#0d9488', color: '#fff', borderRadius: 4, fontSize: '.7rem', padding: '2px 6px', flexShrink: 0}}>{t('campaign.catalogBadge', 'Catalogue')}</span>}
                                        </div>}
                                    {/* Brand-protection cards carry four metrics. They wrap onto a
                                        second line rather than being clipped, so a label is never cut
                                        in half on a narrow card. */}
                                    <div className="campaign-metrics" style={{display: 'flex', gap: 12, rowGap: 10, flexWrap: 'wrap'}}>
                                        <div className="campaign-metric" data-pr-tooltip={t('campaign.productCountHint', 'Bu kampanyadaki aktif ürün sayısı')} data-pr-position="top" style={{cursor: 'default'}}>
                                            <div style={{fontSize: '1.2rem', fontWeight: 800}}>{(c.product_count !== undefined && c.product_count !== null) ? c.product_count : <Skeleton width="2rem"/>}</div>
                                            <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.productCountShort', 'Products')}</small>
                                        </div>
                                        {/* Passive (deactivated) products, neutral gray -- inactive isn't an
                                            error. Buybox cards always show it; elsewhere only when there are
                                            some, so a regular card isn't padded with a zero. */}
                                        {(isMarketplaceBuybox(c) || c.inactive_product_count > 0) &&
                                            <div className="campaign-metric" data-pr-tooltip={t('campaign.passiveHint', 'Deactivated (untracked) products kept in this campaign.')} data-pr-position="top" style={{cursor: 'default'}}>
                                                <div style={{fontSize: '1.2rem', fontWeight: 800, color: '#6b7280'}}>{c.inactive_product_count ?? 0}</div>
                                                <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.passiveShort', 'Passive')}</small>
                                            </div>}
                                        {/* Buybox campaigns source competitors from the marketplace API rather
                                            than manual matching, so "Unmatched" (competitor-suggestion matching)
                                            is meaningless for them. Elsewhere the unmatched count only appears
                                            when there is something to match: a zero says nothing, and it is
                                            also what the card would show while the counts are still loading. */}
                                        {!isMarketplaceBuybox(c) && c.unmatched_competitor_suggestions_count > 0 &&
                                              <div className="campaign-metric" data-pr-tooltip={t('campaign.unmatchedHint', 'Henüz eşleştirilmemiş rakip önerisi sayısı')} data-pr-position="top" style={{cursor: 'default'}}>
                                                <div style={{fontSize: '1.2rem', fontWeight: 800, color: '#dc2626'}}>{c.unmatched_competitor_suggestions_count}</div>
                                                <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.unmatchedShort', 'Eşleşmemiş')}</small>
                                              </div>}
                                        {showsPricingSuggestions(c) &&
                                            <div className="campaign-metric" data-pr-tooltip={t('campaign.priceUpSuggestHint', 'Mevcut fiyatın üzerinde fiyat artışı önerilen ürün sayısı')} data-pr-position="top" style={{cursor: 'default'}}>
                                                <div style={{fontSize: '1.2rem', fontWeight: 800, color: metricColor(stat(c, 'higher'), '#16a34a'), display: 'flex', alignItems: 'center', gap: 4}}>
                                                    <i className="pi pi-arrow-up" style={{fontSize: '.85rem'}}/>
                                                    {stat(c, 'higher') !== undefined ? stat(c, 'higher') : <Skeleton width="1.6rem"/>}
                                                </div>
                                                <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.priceUpSuggestShort', 'Increase')}</small>
                                            </div>}
                                        {showsPricingSuggestions(c) &&
                                            <div className="campaign-metric" data-pr-tooltip={t('campaign.priceDownSuggestHint', 'Mevcut fiyatın altında fiyat azalışı önerilen ürün sayısı')} data-pr-position="top" style={{cursor: 'default'}}>
                                                <div style={{fontSize: '1.2rem', fontWeight: 800, color: metricColor(stat(c, 'lower'), '#d97706'), display: 'flex', alignItems: 'center', gap: 4}}>
                                                    <i className="pi pi-arrow-down" style={{fontSize: '.85rem'}}/>
                                                    {stat(c, 'lower') !== undefined ? stat(c, 'lower') : <Skeleton width="1.6rem"/>}
                                                </div>
                                                <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.priceDownSuggestShort', 'Decrease')}</small>
                                            </div>}
                                        {isBrandProtection(c) &&
                                            <div className="campaign-metric" data-pr-tooltip={t('campaign.mapViolationHint', 'MAP (asgari satış fiyatı) ihlali olan ürün sayısı')} data-pr-position="top" style={{cursor: 'default'}}>
                                                <div style={{fontSize: '1.2rem', fontWeight: 800, color: metricColor(stat(c, 'violatedMap'), '#dc2626')}}>
                                                    {stat(c, 'violatedMap') !== undefined ? stat(c, 'violatedMap') : <Skeleton width="1.6rem"/>}
                                                </div>
                                                <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.mapViolationShort', 'MAP ihlali')}</small>
                                            </div>}
                                        {isBrandProtection(c) &&
                                            <div className="campaign-metric" data-pr-tooltip={t('campaign.mrpViolationHint', 'MRP (azami satış fiyatı) ihlali olan ürün sayısı')} data-pr-position="top" style={{cursor: 'default'}}>
                                                <div style={{fontSize: '1.2rem', fontWeight: 800, color: metricColor(stat(c, 'violatedMrp'), '#dc2626')}}>
                                                    {stat(c, 'violatedMrp') !== undefined ? stat(c, 'violatedMrp') : <Skeleton width="1.6rem"/>}
                                                </div>
                                                <small style={{color: '#6b7280', whiteSpace: 'nowrap', fontSize: '.72rem'}}>{t('campaign.mrpViolationShort', 'MRP ihlali')}</small>
                                            </div>}
                                    </div>
                                    {isCatalog(c) &&
                                        <small style={{color: '#6b7280'}}>
                                            {t('campaign.catalog_prices_from_target')}
                                        </small>}
                                    {/* minHeight reserves the schedule line even when a campaign
                                        has no schedule, so cards in a row stay aligned. */}
                                    {isMarketplaceBuybox(c)
                                        ? <div style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#6b7280', minHeight: 30}}>
                                            <i className="pi pi-clock" style={{fontSize: '.8rem'}}/>
                                            <span>{marketplaceRefreshLabel(c)}</span>
                                          </div>
                                        : (() => {
                                            const info = priceCheckInfo(c);
                                            if (!info) return null;
                                            if (info.off) return (
                                                <div style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#6b7280', minHeight: 30}}>
                                                    <i className="pi pi-clock" style={{fontSize: '.8rem'}}/>
                                                    <span>{t('campaign.priceCheckOff', 'Price check off')}</span>
                                                </div>
                                            );
                                            return (
                                                <div style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', color: '#6b7280', minHeight: 30}}>
                                                    <i className="pi pi-clock" style={{fontSize: '.8rem'}}/>
                                                    <span>
                                                        {t('campaign.priceCheckPerDay', 'Günde {n} fiyat kontrolü').replace('{n}', info.perDay)}
                                                        {info.times.length > 0 && ` · ${info.times.join(', ')}`}
                                                    </span>
                                                </div>
                                            );
                                          })()}
                                    {/* Always-visible action row -- nothing hides behind hover.
                                        All three actions share one outlined shape so they read as a
                                        set; colour alone tells them apart (Setup green, Products
                                        blue, More grey). Setup is always here: a campaign can be
                                        reconfigured at any time, not only while it cannot work yet. */}
                                    <div className="preventSelect" style={{display: 'flex', gap: 8, rowGap: 8, marginTop: 'auto',
                                        alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end',
                                        borderTop: '1px solid #f0f0f0', paddingTop: 12}}>
                                        <Button label={t('menu_item.onboarding', 'Kurulum')} icon="pi pi-compass"
                                                className="p-button-outlined p-button-success p-button-sm"
                                                onClick={(e) => { e.stopPropagation(); openOnboarding(c); }}/>
                                        <Button label={t('menu_item.products', 'Ürünler')} icon="pi pi-arrow-right" iconPos="right"
                                                className="p-button-outlined p-button-sm"
                                                onClick={(e) => { e.stopPropagation(); goToCampaign(c); }}/>
                                        <Button label={t('actions.more', 'Diğer')} icon="pi pi-ellipsis-h"
                                                className="p-button-outlined p-button-secondary p-button-sm" style={{flexShrink: 0}}
                                                onClick={(e) => { e.stopPropagation(); setMenuCampaign(c); cardMenu.current.toggle(e); }}/>
                                    </div>
                                </div>);
                        })}
                    </div>}
            </div>

            {/* per-card overflow menu: edit / delete / reports */}
            <OverlayPanel ref={cardMenu} className="cmpz-menu" style={{width: '20em'}}>
                <style>{`.cmpz-menu .p-overlaypanel-content{padding:8px}.cmpz-menu .cmpz-menu-item{transition:background .12s}.cmpz-menu .cmpz-menu-item:hover{background:#f3f4f6}`}</style>
                {menuCampaign &&
                    <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {/* Edit/Remove are managed in Shopify for Shopify campaigns; hide them here. */}
                        {!isShopifyCampaign(menuCampaign) &&
                            menuItem({icon: 'pi pi-pencil', color: '#2563eb', bg: '#dbeafe',
                                label: t('actions.edit'), desc: t('campaign.menu.editDesc', 'Edit campaign settings'),
                                onClick: () => { cardMenu.current.hide(); edit(menuCampaign); }})}
                        {menuItem({icon: 'pi pi-flag', color: '#d97706', bg: '#fef3c7',
                            label: t('campaign.menu.report', 'Hata Bildir'), desc: t('campaign.menu.reportDesc', 'Bu kampanyayla ilgili bir sorun bildir'),
                            onClick: () => { cardMenu.current.hide(); openReport(menuCampaign); }})}
                        {!isShopifyCampaign(menuCampaign) &&
                            menuItem({icon: 'pi pi-trash', color: '#dc2626', bg: '#fee2e2', danger: true,
                                label: t('actions.remove'), desc: t('campaign.menu.removeDesc', 'Delete this campaign permanently'),
                                onClick: () => { cardMenu.current.hide(); setShowDeleteConfirm(true); setRowData(menuCampaign); }})}

                        {/* reports: a separate group */}
                        <div style={{borderTop: '1px solid #f0f0f0', margin: '8px 4px 4px', paddingTop: 8,
                            fontSize: '.7rem', color: '#9ca3af', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', paddingLeft: 8}}>
                            {t('dashboard.excel_reports', 'Raporlar')}
                        </div>
                        {menuItem({icon: 'pi pi-file-excel', color: '#16a34a', bg: '#dcfce7', loading: loadingExport,
                            label: t('dashboard.excel_product_list'), desc: t('campaign.menu.productListDesc', 'Ürün bilgilerini Excel olarak indir'),
                            onClick: () => { cardMenu.current.hide(); exportDetailedProductList(menuCampaign); }})}
                        {menuItem({icon: 'pi pi-file-excel', color: '#16a34a', bg: '#dcfce7', loading: loadingExport,
                            label: t('dashboard.excel_competitor_list'), desc: t('campaign.menu.competitorListDesc', 'Rakip fiyat karşılaştırmasını indir'),
                            onClick: () => { cardMenu.current.hide(); exportCompetitorList(menuCampaign); }})}
                        {menuCampaign?.settings?.special_fields?.some(sf => sf.name === 'reviews' && sf.selected) &&
                            menuItem({icon: 'pi pi-comments', color: '#7c3aed', bg: '#ede9fe', loading: loadingExport,
                                label: t('dashboard.csv_product_reviews'), desc: t('campaign.menu.reviewsDesc', 'Ürün yorumlarını indir'),
                                onClick: () => { cardMenu.current.hide(); exportProductReviews(menuCampaign); }})}
                        {menuItem({icon: 'pi pi-file-excel', color: '#16a34a', bg: '#dcfce7', loading: loadingExport,
                            label: t('dashboard.product_seller_suggestions'), desc: t('campaign.menu.suggestionsDesc', 'Download product seller suggestions'),
                            onClick: () => { cardMenu.current.hide(); exportProductSuggestions(menuCampaign); }})}
                    </div>}
            </OverlayPanel>

            <Dialog header={t('campaign.report.title', 'Hata Bildir')} visible={showReport} modal style={{width: '34em', maxWidth: '95vw'}}
                    onHide={() => { if (!sendingReport) setShowReport(false); }}
                    footer={
                        <div>
                            <Button label={t('actions.cancel', 'İptal')} className="p-button-text" disabled={sendingReport} onClick={() => setShowReport(false)}/>
                            <Button label={t('campaign.report.send', 'Gönder')} icon="pi pi-send" className="p-button-warning"
                                    loading={sendingReport} disabled={sendingReport || !reportText.trim()} onClick={sendReport}/>
                        </div>
                    }>
                <p style={{marginTop: 0, color: '#6b7280'}}>
                    {t('campaign.report.intro', 'Bu kampanyayla ilgili karşılaştığınız sorunu açıklayın. Ekibimize iletilecektir.')}
                </p>
                {reportCampaign && <div style={{marginBottom: 10, fontWeight: 600}}>{reportCampaign.name}</div>}
                <InputTextarea value={reportText} onChange={(e) => setReportText(e.target.value)} rows={5} autoResize
                               style={{width: '100%'}} placeholder={t('campaign.report.placeholder', 'Sorununuzu buraya yazın…')}/>
            </Dialog>

            {showDeleteConfirm &&
            <Dialog header={t('dialogs.confirm_campaign_delete')} visible={showDeleteConfirm} modal={true} width="600px" onHide={() => {
                setShowDeleteConfirm(false);
                setCampaignNameToDelete("");
            }}>
                <div className="p-flex p-flex-column" style={{flex: '1'}}>
                    <div className="p-text-center">
                        <i className="pi pi-exclamation-triangle" style={{fontSize: '3rem'}}/>

                        <h5>{t('dialogs.confirm_campaign_delete_msg0')}<b>{(rowData ? rowData.name : "")}</b></h5>

                        <p>{t('dialogs.confirm_campaign_delete_msg1')}</p>

                        <p>{t('dialogs.confirm_campaign_delete_msg2')}</p>
                        <br/>
                    </div>
                    <div className="p-grid p-fluid">
                        <div className="p-col-12">
                            <InputText id="campaignNameToDelete" name="campaignNameToDelete" value={campaignNameToDelete} onChange={(e) => {setCampaignNameToDelete(getKeyValue(e).value)}}/>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('yes_no.yes')} icon="pi pi-check" className="p-button-success" onClick={() => {
                                deleteCampaign(rowData);
                                setShowDeleteConfirm(false);
                                setCampaignNameToDelete("");
                            }} />
                        </div>
                        <div className="p-col-6">
                            <Button label={t('yes_no.no')} icon="pi pi-times" className="p-button-secondary" onClick={() => {
                                setShowDeleteConfirm(false);
                                setCampaignNameToDelete("");
                            }} />
                        </div>
                    </div>
                </div>
            </Dialog>}

            <CampaignWizard visible={showWizard} campaigns={campaigns} resume={wizardResume}
                            onSaved={(saved) => updateCampaignInList(saved)}
                            onHide={() => { setShowWizard(false); setWizardResume(null); loadCampaigns(true); }}/>

            <Dialog header={t('campaign.edit_campaign')} visible={showEditDialog} modal
                    style={{width: '34em', maxWidth: '95vw'}}
                    onHide={() => { setShowEditDialog(false); setPage(pages.list); }}>
                <CampaignEdit campaigns={campaigns} page={pages.edit} mode="edit" save={save}
                              returnPageList={() => { setShowEditDialog(false); returnPageList(); }}/>
            </Dialog>

        </React.Fragment>
    );
};
export default Campaign
